import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { MaskedInput } from '@/components/form/masked-input';
import { QueryState } from '@/components/query-state';
import { LoadingState } from '@/components/states';
import { StatusBadge } from '@/components/status-badge';
import { Page } from '@/components/shell/page';
import { EmptyState } from '@/components/empty-state';
import { Field } from '@/components/form/field';
import { FormError } from '@/components/form/form-error';
import { SelectField } from '@/components/form/select-field';
import { optionsFromLabels } from '@/components/form/select-options';
import { SwitchField } from '@/components/form/switch-field';
import { useZodForm } from '@/components/form/use-zod-form';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/features/auth/use-auth';
import { decimalToInput, percentInput } from '@/lib/schemas';
import {
  formatDecimal,
  formatMoney,
  formatPercent,
  fractionToPercentInput,
  parseMoneyInput,
  parsePercentInput,
} from '@/lib/money';
import { toast, toastApiError } from '@/lib/toast';
import {
  type AdditionalCostItem,
  type PackageItem,
  type PricingSettings,
  ROUNDING_LABELS,
  type Rounding,
  useAdditionalCosts,
  usePackages,
  usePricingSettings,
  useSaveAdditionalCost,
  useSaveSettings,
} from '../api/costs';
import { DeletePackageDialog } from '../components/delete-package-dialog';
import { PackageModal } from '../components/package-modal';

const ROUNDING_OPTIONS = optionsFromLabels<Rounding>(ROUNDING_LABELS);
const COST_KIND_OPTIONS = optionsFromLabels<AdditionalCostItem['kind']>({
  PERCENT_OF_PRICE: '% do preço',
  FIXED_PER_UNIT: 'R$ por venda',
});

/** Fração da API ("0.16") para o campo em percentual ("16"). */
export function CostsPage() {
  const { can } = useAuth();
  const canEdit = can('catalog.write');
  return (
    <Page>
      <PageHeader
        title="Custos e metas"
        description="O que entra na conta de cada venda além do custo do produto."
      />
      <SettingsCard canEdit={can('organization.manage')} />
      <PackagesCard canEdit={canEdit} />
      <AdditionalCostsCard canEdit={canEdit} />
    </Page>
  );
}

const settingsSchema = z.object({
  targetMargin: percentInput({ required: false }),
  minimumMargin: percentInput({ required: false }),
  rounding: z.enum(Object.keys(ROUNDING_LABELS) as [Rounding, ...Rounding[]]),
  packageClearanceCm: z
    .string()
    .trim()
    .transform((value) => (value === '' ? '0' : value.replace(',', '.')))
    .refine((value) => /^\d{1,3}(\.\d{1,2})?$/.test(value), 'Folga inválida. Ex.: 0 ou 1,5.'),
  weightTolerance: percentInput({ required: true }),
  hideOutOfStockFindings: z.boolean(),
});

function SettingsCard({ canEdit }: { canEdit: boolean }) {
  const settings = usePricingSettings();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Metas de margem</CardTitle>
      </CardHeader>
      <CardContent>
        <QueryState query={settings} loading={<LoadingState rows={3} />}>
          {/* A chave recria o formulário quando o dado salvo muda: nada de copiar o dado do
              servidor para o estado da tela à mão. */}
          {(data) => <SettingsForm key={JSON.stringify(data)} settings={data} canEdit={canEdit} />}
        </QueryState>
      </CardContent>
    </Card>
  );
}

function SettingsForm({ settings, canEdit }: { settings: PricingSettings; canEdit: boolean }) {
  const save = useSaveSettings();
  const form = useZodForm(settingsSchema, {
    targetMargin: fractionToPercentInput(settings.targetMargin),
    minimumMargin: fractionToPercentInput(settings.minimumMargin),
    rounding: settings.rounding,
    packageClearanceCm: decimalToInput(settings.packageClearanceCm),
    weightTolerance: fractionToPercentInput(settings.weightTolerance),
    hideOutOfStockFindings: settings.hideOutOfStockFindings,
  });

  const submit = form.handleSubmit(async (values) => {
    if (values.weightTolerance === null) return;
    const saved = await save
      .mutateAsync({ ...values, weightTolerance: values.weightTolerance })
      .then(() => true)
      .catch(() => false);
    if (saved) toast.success('Metas salvas. As margens estão sendo recalculadas.');
  });

  const control = form.control;
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
      <FormError error={save.error} fallback="Não foi possível salvar." className="sm:col-span-2" />
      <Field
        control={control}
        name="targetMargin"
        label="Margem líquida desejada (%)"
        hint="Base do preço sugerido."
      >
        {(field) => <MaskedInput {...field} mask="percent" disabled={!canEdit} />}
      </Field>
      <Field
        control={control}
        name="minimumMargin"
        label="Margem mínima (%)"
        hint="Abaixo dela, o anúncio vira alerta."
      >
        {(field) => <MaskedInput {...field} mask="percent" disabled={!canEdit} />}
      </Field>
      <SelectField
        control={control}
        name="rounding"
        label="Arredondamento do preço sugerido"
        options={ROUNDING_OPTIONS}
        disabled={!canEdit}
      />
      <Field
        control={control}
        name="packageClearanceCm"
        label="Folga da embalagem (cm)"
        hint="Somada a cada medida do produto no encaixe."
      >
        {(field) => <MaskedInput {...field} mask="measure" disabled={!canEdit} />}
      </Field>
      <Field
        control={control}
        name="weightTolerance"
        label="Tolerância de peso divergente (%)"
        required
        hint="Diferença de peso a partir da qual vira alerta — entre lojas e em relação ao ERP. Sugerido: 10."
      >
        {(field) => <MaskedInput {...field} mask="percent" disabled={!canEdit} />}
      </Field>
      <SwitchField
        control={control}
        name="hideOutOfStockFindings"
        label="Central de Ações sem itens de estoque zerado"
        description="Anúncio sem estoque não vende agora: os alertas dele saem da Central, que mostra quantos ficaram de fora. Sugerido: ligado."
        disabled={!canEdit}
        className="sm:col-span-2"
      />
      {canEdit && (
        <div className="sm:col-span-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar metas'}
          </Button>
        </div>
      )}
    </form>
  );
}

function PackagesCard({ canEdit }: { canEdit: boolean }) {
  const packages = usePackages();
  const [editing, setEditing] = useState<PackageItem | 'new' | null>(null);
  const [deleting, setDeleting] = useState<PackageItem | null>(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Embalagens padrão</CardTitle>
        {canEdit && (
          <Button variant="outline" onClick={() => setEditing('new')}>
            <Plus aria-hidden /> Nova embalagem
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-sm text-muted-foreground">
          Cada anúncio recebe a embalagem mais barata em que as suas medidas cabem (em qualquer
          posição). No produto, dá para fixar outra.
        </p>
        <QueryState
          query={packages}
          loading={<LoadingState rows={2} />}
          isEmpty={(data) => data.length === 0}
          empty={
            <EmptyState
              title="Nenhuma embalagem"
              description="Sem embalagem, a margem sai incompleta."
            />
          }
        >
          {(data) => (
            <ul className="divide-y">
              {data.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-4 py-2 text-sm">
                  <span>
                    <span className="font-medium">{item.name}</span>{' '}
                    <span className="text-muted-foreground">
                      {item.lengthCm && item.widthCm && item.heightCm
                        ? `${formatDecimal(item.lengthCm, 1)} × ${formatDecimal(item.widthCm, 1)} × ${formatDecimal(item.heightCm, 1)} cm`
                        : 'sem medidas · escolhida no produto'}
                    </span>{' '}
                    {!item.active && <StatusBadge tone="neutral">inativa</StatusBadge>}
                    {item.duplicateOf && (
                      <StatusBadge tone="warning">
                        mesmas medidas de {item.duplicateOf.name}
                      </StatusBadge>
                    )}
                    {(item.productCount ?? 0) > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {' '}
                        · fixada em {item.productCount} produto(s)
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="tabular">{formatMoney(item.cost)}</span>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Editar ${item.name}`}
                        onClick={() => setEditing(item)}
                      >
                        <Pencil aria-hidden />
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Excluir ${item.name}`}
                        onClick={() => setDeleting(item)}
                      >
                        <Trash2 aria-hidden />
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </QueryState>
      </CardContent>
      <PackageModal value={editing} onClose={() => setEditing(null)} />
      <DeletePackageDialog
        value={deleting}
        packages={packages.data ?? []}
        onClose={() => setDeleting(null)}
      />
    </Card>
  );
}

const additionalSchema = z
  .object({
    name: z.string().trim().min(2, 'Dê um nome ao custo.').max(80),
    kind: z.enum(['PERCENT_OF_PRICE', 'FIXED_PER_UNIT']),
    value: z.string(),
  })
  .transform((input, context) => {
    // Percentual vira fração ("2" → "0.02"); valor em reais continua texto decimal.
    const parsed =
      input.kind === 'PERCENT_OF_PRICE'
        ? parsePercentInput(input.value)
        : parseMoneyInput(input.value);
    if (parsed === null) {
      context.addIssue({
        code: 'custom',
        path: ['value'],
        message:
          input.kind === 'PERCENT_OF_PRICE'
            ? 'Percentual inválido. Ex.: 2 ou 1,5.'
            : 'Valor inválido. Ex.: 1,50.',
      });
      return z.NEVER;
    }
    return { ...input, value: parsed };
  });

function AdditionalCostsCard({ canEdit }: { canEdit: boolean }) {
  const costs = useAdditionalCosts();
  const save = useSaveAdditionalCost();
  const form = useZodForm(additionalSchema, { name: '', kind: 'PERCENT_OF_PRICE', value: '' });
  const kind = form.watch('kind');

  const submit = form.handleSubmit(async (values) => {
    const saved = await save
      .mutateAsync({ ...values, active: true })
      .then(() => true)
      .catch(() => false);
    if (!saved) return;
    form.reset({ name: '', kind: values.kind, value: '' });
    toast.success('Custo adicional salvo.');
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custos adicionais</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Entram em toda venda: taxa de antecipação, rateio operacional, etiqueta…
        </p>
        <QueryState query={costs} loading={<LoadingState rows={2} />}>
          {(data) => (
            <ul className="divide-y">
              {data.map((cost) => (
                <li key={cost.id} className="flex items-center justify-between gap-4 py-2 text-sm">
                  <span>
                    {cost.name} {!cost.active && <StatusBadge tone="neutral">inativo</StatusBadge>}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="tabular">
                      {cost.kind === 'PERCENT_OF_PRICE'
                        ? `${formatPercent(cost.value)} do preço`
                        : `${formatMoney(cost.value)} por venda`}
                    </span>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          save
                            .mutateAsync({
                              id: cost.id,
                              name: cost.name,
                              kind: cost.kind,
                              value: cost.value,
                              active: !cost.active,
                            })
                            .catch((error: unknown) => toastApiError(error))
                        }
                      >
                        {cost.active ? 'Inativar' : 'Ativar'}
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </QueryState>
        {canEdit && (
          <form onSubmit={submit} className="grid gap-3 sm:grid-cols-4" noValidate>
            <FormError
              error={save.error}
              fallback="Não foi possível salvar."
              className="sm:col-span-4"
            />
            <Field control={form.control} name="name" label="Nome" className="sm:col-span-2">
              {(field) => <Input {...field} />}
            </Field>
            <SelectField
              control={form.control}
              name="kind"
              label="Tipo"
              options={COST_KIND_OPTIONS}
            />
            <Field
              control={form.control}
              name="value"
              label={kind === 'PERCENT_OF_PRICE' ? 'Percentual' : 'Valor (R$)'}
            >
              {(field) => (
                <MaskedInput {...field} mask={kind === 'PERCENT_OF_PRICE' ? 'percent' : 'money'} />
              )}
            </Field>
            <div className="sm:col-span-4">
              <Button type="submit" variant="outline" disabled={save.isPending}>
                <Plus aria-hidden /> Adicionar
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
